import { useState, useRef, useCallback, useEffect } from 'react'

export interface UseScreenRecorderReturn {
    isRecording: boolean
    startRecording: (withWebcam?: boolean) => Promise<void>
    stopRecording: () => void
    webcamStream: MediaStream | null
    recordedBlob: Blob | null
    clearRecording: () => void
    error: { type: 'permission' | 'unknown'; message: string } | null
    clearError: () => void
}

// Detect best supported MIME type
function getSupportedMimeType(): string {
    const types = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=h264,opus',
        'video/webm',
        'video/mp4',
    ]
    for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) return type
    }
    return ''
}

export function useScreenRecorder(): UseScreenRecorderReturn {
    const [isRecording, setIsRecording] = useState(false)
    const [webcamStream, setWebcamStream] = useState<MediaStream | null>(null)
    const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null)
    const [error, setError] = useState<{ type: 'permission' | 'unknown'; message: string } | null>(null)

    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const audioContextRef = useRef<AudioContext | null>(null)
    const allStreamsRef = useRef<MediaStream[]>([])  // Track all streams for cleanup
    const isStoppingRef = useRef(false)              // Guard against double-stop
    const recordingStartTimeRef = useRef<number>(0)  // Track start time for minimum duration

    const clearError = useCallback(() => setError(null), [])

    const clearRecording = useCallback(() => {
        setRecordedBlob(null)
        chunksRef.current = []
        setError(null)
    }, [])

    const cleanupStreams = useCallback(() => {
        allStreamsRef.current.forEach(stream => {
            stream.getTracks().forEach(t => t.stop())
        })
        allStreamsRef.current = []

        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => { })
            audioContextRef.current = null
        }

        setWebcamStream(null)
    }, [])

    const startRecording = useCallback(async (withWebcam: boolean = true) => {
        // Prevent starting if already recording
        if (isStoppingRef.current || mediaRecorderRef.current?.state === 'recording') return

        setError(null)
        chunksRef.current = []
        allStreamsRef.current = []

        try {
            const audioContext = new AudioContext()
            audioContextRef.current = audioContext
            const destination = audioContext.createMediaStreamDestination()

            let userMediaStream: MediaStream | null = null

            // 1. Get User Media (Mic + Optional Camera)
            try {
                const constraints: MediaStreamConstraints = {
                    audio: true,
                    video: withWebcam ? { width: 320, height: 240 } : false
                }

                userMediaStream = await navigator.mediaDevices.getUserMedia(constraints)
                allStreamsRef.current.push(userMediaStream)

                const videoTracks = userMediaStream.getVideoTracks()
                if (withWebcam && videoTracks.length > 0) {
                    setWebcamStream(userMediaStream)
                }

                const audioTracks = userMediaStream.getAudioTracks()
                if (audioTracks.length > 0) {
                    const micSource = audioContext.createMediaStreamSource(userMediaStream)
                    micSource.connect(destination)
                }
            } catch (err: any) {
                console.warn("Mic/Camera access denied or error:", err)
                // Continue without mic — screen-only recording is still valid
            }

            // 2. Get Screen (with System Audio)
            const screen = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: true
            })
            allStreamsRef.current.push(screen)

            // Add System Audio to Mix
            if (screen.getAudioTracks().length > 0) {
                const systemSource = audioContext.createMediaStreamSource(screen)
                systemSource.connect(destination)
            }

            // 3. Combine Tracks
            const tracks = [...screen.getVideoTracks()]
            const mixedAudioTrack = destination.stream.getAudioTracks()[0]
            if (mixedAudioTrack) {
                tracks.push(mixedAudioTrack)
            } else if (screen.getAudioTracks().length > 0) {
                // Fallback: use raw screen audio if AudioContext produced nothing
                tracks.push(...screen.getAudioTracks())
            }

            const combinedStream = new MediaStream(tracks)
            allStreamsRef.current.push(combinedStream)

            const mimeType = getSupportedMimeType()
            const mediaRecorder = new MediaRecorder(
                combinedStream,
                mimeType ? { mimeType } : undefined
            )
            mediaRecorderRef.current = mediaRecorder
            isStoppingRef.current = false

            // Collect chunks every second — this is the KEY fix.
            // Without a timeslice, ondataavailable only fires ONCE on stop,
            // and if it's delayed or missed, the blob is empty.
            mediaRecorder.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) {
                    chunksRef.current.push(e.data)
                }
            }

            mediaRecorder.onstop = () => {
                // Chrome (Chromium) has a known bug where the final ondataavailable
                // event is queued AFTER onstop fires, even though the spec guarantees
                // the opposite order. A setTimeout(0) defers blob assembly until all
                // queued microtasks and data events have been processed, ensuring
                // chunksRef.current is fully populated before we build the blob.
                setTimeout(() => {
                    const blobType = mimeType.split(';')[0] || 'video/webm'
                    const blob = new Blob(chunksRef.current, { type: blobType })

                    if (blob.size > 0) {
                        setRecordedBlob(blob)
                    } else {
                        console.error("Recording produced an empty blob — no data was captured.")
                        setError({
                            type: 'unknown',
                            message: "Recording failed: no data was saved. Please try again."
                        })
                    }

                    cleanupStreams()
                    setIsRecording(false)
                    isStoppingRef.current = false
                }, 0)
            }

            mediaRecorder.onerror = (e) => {
                console.error("MediaRecorder error:", e)
                setError({ type: 'unknown', message: "A recording error occurred." })
                cleanupStreams()
                setIsRecording(false)
                isStoppingRef.current = false
            }

            // Handle user stopping via browser's native "Stop sharing" button
            screen.getVideoTracks()[0].addEventListener('ended', () => {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                    isStoppingRef.current = true
                    mediaRecorderRef.current.stop()
                }
            })

            // Use 250ms timeslice so chunks start flowing well before the first second.
            // This prevents an empty blob when the user stops recording quickly,
            // since Chrome produces no data on stop() if no timeslice has fired yet.
            recordingStartTimeRef.current = Date.now()
            mediaRecorder.start(250)
            setIsRecording(true)

        } catch (err: any) {
            console.error("Error starting recording:", err)
            cleanupStreams()
            setIsRecording(false)
            isStoppingRef.current = false

            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setError({
                    type: 'permission',
                    message: "Permission to access screen or microphone was denied. Please allow access in your browser settings."
                })
            } else if (err.name !== 'AbortError') {
                // User cancelled the picker — don't show an error
                setError({
                    type: 'unknown',
                    message: "Failed to start recording. Please try again."
                })
            }
        }
    }, [cleanupStreams])

    const stopRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current
        if (!recorder || recorder.state === 'inactive' || isStoppingRef.current) return

        isStoppingRef.current = true

        const elapsed = Date.now() - recordingStartTimeRef.current
        const MIN_DURATION_MS = 600

        if (elapsed < MIN_DURATION_MS) {
            // Too short — wait until at least 600ms of data has been captured.
            // With a 250ms timeslice, at least two chunks will have been flushed by then.
            setTimeout(() => {
                if (recorder.state !== 'inactive') recorder.stop()
            }, MIN_DURATION_MS - elapsed)
        } else {
            recorder.stop()
        }
    }, [])

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            const recorder = mediaRecorderRef.current
            if (recorder && recorder.state !== 'inactive') {
                recorder.stop()
            }
            cleanupStreams()
        }
    }, [cleanupStreams])

    return {
        isRecording,
        startRecording,
        stopRecording,
        webcamStream,
        recordedBlob,
        clearRecording,
        error,
        clearError
    }
}
